import React from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Button,
  Row,
  Col,
  Badge,
  Form,
  FormGroup,
  FormFeedback,
  Label,
  Input,
  Spinner,
  Alert,
  Progress,
  Table,
  Nav,
  NavItem,
  NavLink
} from 'reactstrap';
import ReactBSAlert from 'react-bootstrap-sweetalert';
import SimpleHeader from '@/components/Headers/SimpleHeader';

/**
 * One page_content entry. The container holds them as `Record<string, unknown>`;
 * this view reads the template variable's name and its value, which is a string
 * or structured JSON.
 */
export type TranslatePageContentEntry = { variable?: string; value?: unknown };

/** The step-wizard form, as UpdateTranslatePageContainer.tsx holds it in state. */
export type UpdateTranslateFormData = {
  global_title: string;
  meta_keywords: string;
  meta_description: string;
  intro_text: string;
  target_language: string;
  requires_review: boolean;
  publish_statement: boolean;
  page_content: TranslatePageContentEntry[];
};

/** The translated page as this view reads it; every field past `_id` may be absent. */
export type UpdateTranslatePageRecord = {
  _id: string;
  page_configuration?: { type?: string; path?: string; object_target?: string };
  parentPage?: {
    global_title?: string;
    site?: { domain?: string; language_code?: string };
    page_configuration?: { type?: string };
  };
  page_template?: { name?: string };
};

/** SCRUM-1509: unparseable text for a structured variable, held per page_content index. */
type JsonDraft = { text: string; valid: boolean };

export type UpdateTranslatePageViewProps = {
  loading: boolean;
  saving: boolean;
  translatePage: UpdateTranslatePageRecord | null;
  /** The store's `translatedpages.page`; passed through, not read by this view. */
  fullPage?: unknown;
  formData: UpdateTranslateFormData;
  currentStep: number;
  onInputChange: <K extends keyof UpdateTranslateFormData>(
    field: K,
    value: UpdateTranslateFormData[K]
  ) => void;
  onPageContentChange: (index: number, field: string, value: unknown) => void;
  onSave?: (requiresReview?: boolean) => void;
  onCancel: () => void;
  onPreview: () => void;
  onNextStep: () => void;
  onPrevStep: () => void;
  alert?: React.ReactNode;
  onShowSaveConfirmation: () => void;
};

const UpdateTranslatePageView = ({
  loading,
  saving,
  translatePage,
  fullPage,
  formData,
  currentStep,
  onInputChange,
  onPageContentChange,
  onSave,
  onCancel,
  onPreview,
  onNextStep,
  onPrevStep,
  alert,
  onShowSaveConfirmation
}: UpdateTranslatePageViewProps) => {
  // Unparseable text for a structured (non-string) variable is held here, keyed by the
  // page_content index, and never written into page_content. Before SCRUM-1509 the JSON
  // input's catch wrote the raw text into the entry's `value`, so the first keystroke of
  // any edit — every intermediate state of a JSON document is invalid — replaced the
  // stored structure with a string, and Save persisted it.
  const [jsonDrafts, setJsonDrafts] = React.useState<Record<number, JsonDraft>>({});
  const hasInvalidJson = Object.values(jsonDrafts).some((d) => d && !d.valid);

  // The drafts belong to the page they were typed on. The container swaps
  // translatePage and page_content in place when the route id changes (its effect
  // on [translatedPages, id]) without remounting this view, so a draft left on row
  // N of page A would otherwise be shown — and, on the next parseable keystroke,
  // committed — as row N of page B (SCRUM-1557). Keyed on the page id rather than
  // on page_content identity: the container hands over a new array on every
  // parseable keystroke, and resetting on that would replace the text being typed
  // with its re-serialised value.
  const translatePageId = translatePage?._id;
  React.useEffect(() => {
    // Same object back when there is nothing to drop, so the mount (and
    // StrictMode's double-invoke) does not schedule an extra render.
    setJsonDrafts((prev) => (Object.keys(prev).length === 0 ? prev : {}));
  }, [translatePageId]);

  const supportedLanguages = {
    nl: 'Dutch',
    pt: 'Portuguese',
    fr: 'French',
    de: 'German',
    es: 'Spanish',
    en: 'English',
    it: 'Italian',
    pl: 'Polish',
    dk: 'Danish'
  };

  if (loading) {
    return (
      <>
        <SimpleHeader name="Update Translated Page" parentName="Translation Pages" />
        <Container className="mt--6" fluid>
          <div className="text-center py-5">
            <Spinner color="primary" size="lg" />
            <div className="mt-3 h5">Loading translated page...</div>
          </div>
        </Container>
      </>
    );
  }

  if (!translatePage) {
    return (
      <>
        <SimpleHeader name="Update Translated Page" parentName="Translation Pages" />
        <Container className="mt--6" fluid>
          <Alert color="warning" className="text-center">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Translated page not found
          </Alert>
        </Container>
      </>
    );
  }

  // Step 1: Title & Description
  const renderStep1 = () => (
    <Card>
      <CardHeader>
        <div className="d-flex align-items-center">
          <h3 className="mb-0">Step 1: Page Title & Description</h3>
        </div>
      </CardHeader>
      <CardBody>
        <Form>
          {/* Page Title */}
          <FormGroup>
            <Label for="globalTitle" className="form-control-label">
              Page Title
            </Label>
            <Input
              type="text"
              id="globalTitle"
              value={formData.global_title || ''}
              onChange={(e) => onInputChange('global_title', e.target.value)}
              placeholder="Enter translated page title..."
              className="form-control-alternative"
            />
          </FormGroup>

          {/* Meta Description */}
          <FormGroup>
            <Label for="metaDescription" className="form-control-label">
               Description
            </Label>
            <Input
              type="textarea"
              id="metaDescription"
              rows="4"
              value={formData.meta_description || ''}
              onChange={(e) => onInputChange('meta_description', e.target.value)}
              placeholder="Enter meta description for SEO..."
              className="form-control-alternative"
            />
            <small className="text-muted">
              dev stage by FIras..
            </small>
          </FormGroup>

       

        
        </Form>
      </CardBody>
    </Card>
  );

  // Step 2: Page Content
  const renderStep2 = () => (
    <Card>
      <CardHeader>
        <div className="d-flex align-items-center">
          <i className="fas fa-code text-success me-2" style={{ fontSize: '1.5rem' }}></i>
          <h3 className="mb-0">Step 2: Page Content Variables</h3>
        </div>
      </CardHeader>
      <CardBody>
        {!formData.page_content || formData.page_content.length === 0 ? (
          <Alert color="info">
            <i className="fas fa-info-circle me-2"></i>
            No page content variables found for this translated page.
          </Alert>
        ) : (
          <div>
            <p className="text-muted mb-4">
              <i className="fas fa-info-circle me-2"></i>
              Edit the content variables below. These control the dynamic content on your page.
            </p>

            <div className="table-responsive">
              <Table>
                <thead className="thead-light">
                  <tr>
                    <th>Variable</th>
                    <th>Content</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.page_content.map((item, index) => (
                    // Static list (SCRUM-2184): rows are never added or removed here, and jsonDrafts and onPageContentChange address a row by this index.
                    <tr key={index}>
                      <td>
                        <Badge color="info" className="text-uppercase">
                          {item.variable || `Variable ${index + 1}`}
                        </Badge>
                      </td>
                      <td>
                        {typeof item.value === 'string' ? (
                          <Input
                            type="textarea"
                            value={item.value || ''}
                            onChange={(e) => onPageContentChange(index, 'value', e.target.value)}
                            placeholder={`Enter content for ${item.variable || 'this variable'}...`}
                            rows="2"
                          />
                        ) : (
                          <>
                            <Input
                              type="text"
                              value={
                                jsonDrafts[index]
                                  ? jsonDrafts[index].text
                                  : JSON.stringify(item.value) || ''
                              }
                              invalid={Boolean(jsonDrafts[index] && !jsonDrafts[index].valid)}
                              onChange={(e) => {
                                const text = e.target.value;
                                let parsedValue;
                                try {
                                  parsedValue = JSON.parse(text);
                                } catch (error) {
                                  // Hold the text locally and leave the stored value alone.
                                  setJsonDrafts((prev) => ({
                                    ...prev,
                                    [index]: { text, valid: false }
                                  }));
                                  return;
                                }
                                setJsonDrafts((prev) => ({
                                  ...prev,
                                  [index]: { text, valid: true }
                                }));
                                onPageContentChange(index, 'value', parsedValue);
                              }}
                              placeholder="Enter JSON or text value..."
                            />
                            {jsonDrafts[index] && !jsonDrafts[index].valid && (
                              <FormFeedback>
                                Not valid JSON — the saved value is unchanged until this parses.
                              </FormFeedback>
                            )}
                          </>
                        )}
                      </td>
                      <td>
                        <Badge color="secondary">
                          {typeof item.value}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );

  // Step 3: Page Configuration & Settings
  const renderStep3 = () => (
    <Card>
      <CardHeader>
        <div className="d-flex align-items-center">
          <i className="fas fa-cog text-warning me-2" style={{ fontSize: '1.5rem' }}></i>
          <h3 className="mb-0">Step 3: Configuration & Settings</h3>
        </div>
      </CardHeader>
      <CardBody>
        <Row>
          <Col md="8">
            {/* Page Configuration */}
            {translatePage.page_configuration && (
              <div className="mb-4">
                <h5 className="mb-3">
                  <i className="fas fa-wrench me-2"></i>
                  Page Configuration
                </h5>
                <Table size="sm">
                  <tbody>
                    <tr>
                      <td><strong>Page Type:</strong></td>
                      <td>
                        <Badge color="primary">
                          {translatePage.page_configuration.type || 'N/A'}
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Path:</strong></td>
                      <td>
                        <code>{translatePage.page_configuration.path || 'N/A'}</code>
                      </td>
                    </tr>
                    {translatePage.page_configuration.object_target && (
                      <tr>
                        <td><strong>Target Object:</strong></td>
                        <td>{translatePage.page_configuration.object_target}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            )}

          
          </Col>

          <Col md="4">
            {/* Translation Settings */}
            <div className="mb-4">
              <h5 className="mb-3">
                <i className="fas fa-toggle-on me-2"></i>
                Translation Settings
              </h5>

              <FormGroup check className="mb-3">
                <Label check>
                  <Input
                    type="checkbox"
                    checked={formData.requires_review || false}
                    onChange={(e) => onInputChange('requires_review', e.target.checked)}
                  />
                  <span className="form-check-sign"></span>
                  Requires Review
                </Label>
                <small className="text-muted d-block">
                  Mark if this translation needs review before publishing
                </small>
              </FormGroup>

              <FormGroup check>
                <Label check>
                  <Input
                    type="checkbox"
                    checked={formData.publish_statement || false}
                    onChange={(e) => onInputChange('publish_statement', e.target.checked)}
                  />
                  <span className="form-check-sign"></span>
                  Published
                </Label>
                <small className="text-muted d-block">
                  Make this translation live on the website
                </small>
              </FormGroup>
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );

  const getStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return renderStep1();
    }
  };

  return (
    <>
      {alert}
      <SimpleHeader name="Update Translated Page" parentName="Translation Pages" />
      <Container className="mt--6" fluid>
        <Row>
          <Col xl="8">
            {/* Step Navigation */}
            <Card className="mb-4">
              <CardBody>
                <Nav pills className="justify-content-center">
                  <NavItem>
                    <NavLink
                      className={`${currentStep === 1 ? 'active' : ''} ${currentStep > 1 ? 'text-success' : ''}`}
                      style={{ cursor: 'default' }}
                    >
                      {currentStep > 1 ? <i className="fas fa-check me-2"></i> : <span className="me-2">1</span>}
                      Title & Description
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={`${currentStep === 2 ? 'active' : ''} ${currentStep > 2 ? 'text-success' : ''}`}
                      style={{ cursor: 'default' }}
                    >
                      {currentStep > 2 ? <i className="fas fa-check me-2"></i> : <span className="me-2">2</span>}
                      Page Content
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={currentStep === 3 ? 'active' : ''}
                      style={{ cursor: 'default' }}
                    >
                      <span className="me-2">3</span>
                      Configuration
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardBody>
            </Card>

            {/* Step Content */}
            {getStepContent()}

            {/* Navigation Buttons */}
            <Card>
              <CardBody>
                {hasInvalidJson && (
                  <Alert color="warning">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    One or more content variables hold text that is not valid JSON. Their
                    saved values are unchanged; correct them on step 2 to enable saving.
                  </Alert>
                )}
                <div className="d-flex justify-content-between">
                  <div>
                    {currentStep > 1 && (
                      <Button
                        color="secondary"
                        onClick={onPrevStep}
                        disabled={saving}
                      >
                        <i className="fas fa-arrow-left me-2"></i>
                        Previous
                      </Button>
                    )}
                  </div>

                  <div>
                    {currentStep < 3 ? (
                      <Button
                        color="primary"
                        onClick={onNextStep}
                        disabled={saving}
                      >
                        Next
                        <i className="fas fa-arrow-right ms-2"></i>
                      </Button>
                    ) : (
                      <Button
                        color="success"
                        onClick={onShowSaveConfirmation}
                        disabled={saving || hasInvalidJson}
                        size="lg"
                      >
                        {saving ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Saving...
                          </>
                        ) : (
                          <>
                            Save All Changes
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl="4">
            {/* Source Page Info */}
            <Card className="mb-4">
              <CardHeader>
                <h3 className="mb-0">
                  <i className="fas fa-info-circle text-info me-2"></i>
                  Source Page Info
                </h3>
              </CardHeader>
              <CardBody>
                <Table size="sm" className="mb-0">
                  <tbody>
                    <tr>
                      <td className="border-0 py-2"><strong>Original Title:</strong></td>
                      <td className="border-0 py-2">
                        {translatePage.parentPage?.global_title || 'N/A'}
                      </td>
                    </tr>
                    <tr>
                      <td className="border-0 py-2"><strong>Source Site:</strong></td>
                      <td className="border-0 py-2">
                        {translatePage.parentPage?.site?.domain || 'N/A'}
                      </td>
                    </tr>
                    <tr>
                      <td className="border-0 py-2"><strong>Source Language:</strong></td>
                      <td className="border-0 py-2">
                        <Badge color="primary" pill>
                          {translatePage.parentPage?.site?.language_code?.toUpperCase() || 'N/A'}
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <td className="border-0 py-2"><strong>Page Type:</strong></td>
                      <td className="border-0 py-2">
                        <Badge color="secondary">
                          {translatePage.parentPage?.page_configuration?.type || 'N/A'}
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <td className="border-0 py-2"><strong>Template:</strong></td>
                      <td className="border-0 py-2">
                        {translatePage.page_template?.name || 'N/A'}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </CardBody>
            </Card>

            {/* Progress */}
            <Card>
              <CardBody>
                <div className="text-center">
                  <div className="h2 mb-2">Step {currentStep} of 3</div>
                  <Progress value={(currentStep / 3) * 100} className="mb-3" />
                  <div className="d-grid gap-2">
                    <Button color="info" onClick={onPreview} size="sm">
                      <i className="fas fa-eye me-2"></i>
                      Preview
                    </Button>
                    <Button color="secondary" onClick={onCancel} size="sm">
                      <i className="fas fa-arrow-left me-2"></i>
                      Back to List
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default UpdateTranslatePageView;